import axios from "axios";
import Cookie from "js-cookie";
import logoDark from "@/assets/images/logo-dark.png";
import logoLight from "@/assets/images/logo-light.png";

let isHandling401 = false;

export function setupAxiosInterceptors() {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response && error.response.status === 401) {
        const currentPath = window.location.pathname;

        // Jangan intercept jika sedang berada di rute login atau SSO callback
        if (currentPath.includes("/auth/callback") || currentPath.includes("/login")) {
          return Promise.reject(error);
        }

        // Mencegah multiple alert jika ada banyak request paralel yang return 401 sekaligus
        if (!isHandling401) {
          isHandling401 = true;

          // 1. Simpan halaman terakhir user agar setelah SSO callback kembali ke sini
          const redirectUrl = window.location.pathname + window.location.search + window.location.hash;
          if (redirectUrl && !redirectUrl.includes("/login") && !redirectUrl.includes("/auth/callback")) {
            sessionStorage.setItem("sso_redirect_url", redirectUrl);
            Cookie.set("sso_redirect_url", redirectUrl, { expires: 1, path: "/" });
          }

          // 2. Hapus sesi lokal yang sudah kedaluwarsa
          Cookie.remove("saa_token");
          Cookie.remove("user");

          // Ekstrak origin backend secara aman tanpa bug split('/api')
          const rawBackendUrl = process.env.VUE_APP_BACKEND_API_URL || window.location.origin;
          const backendOrigin = new URL(rawBackendUrl).origin;
          const ssoUrl = `${backendOrigin}/auth/sso`;

          // 3. Pasang Fullscreen Solid Overlay secara instan agar halaman Sales tertutup 100%
          if (typeof document !== "undefined") {
            const savedTheme = localStorage.getItem("theme-mode") || localStorage.getItem("data-bs-theme");
            const isDark = savedTheme === "dark";
            const logoSrc = isDark ? logoLight : logoDark;
            const overlay = document.createElement("div");
            overlay.id = "sso-auth-overlay";
            overlay.style.cssText = `
              position: fixed;
              top: 0;
              left: 0;
              width: 100vw;
              height: 100vh;
              background: ${isDark ? "radial-gradient(circle at 50% 25%, rgba(59, 130, 246, 0.08) 0%, rgba(15, 23, 42, 0) 65%), #0f172a" : "radial-gradient(circle at 50% 25%, rgba(37, 99, 235, 0.05) 0%, rgba(248, 250, 252, 0) 65%), #f8fafc"};
              z-index: 99999999;
              display: flex;
              align-items: center;
              justify-content: center;
              font-family: inherit;
              padding: 1.5rem;
              box-sizing: border-box;
            `;

            overlay.innerHTML = `
              <style>
                @keyframes ssoSvgRotate { 100% { transform: rotate(360deg); } }
                @keyframes ssoSvgDash {
                  0% { stroke-dasharray: 1, 150; stroke-dashoffset: 0; }
                  50% { stroke-dasharray: 90, 150; stroke-dashoffset: -35; }
                  100% { stroke-dasharray: 90, 150; stroke-dashoffset: -124; }
                }
                @keyframes ssoCardEntrance {
                  from { opacity: 0; transform: translateY(12px) scale(0.99); }
                  to { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes ssoPulseDot {
                  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
                  70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
                  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
                }
              </style>
              <div style="
                max-width: 440px;
                width: 100%;
                background: ${isDark ? "#1e2229" : "#ffffff"};
                border: 1px solid ${isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(226, 232, 240, 0.85)"};
                border-radius: 24px;
                box-shadow: ${isDark ? "0 25px 50px -12px rgba(0, 0, 0, 0.45)" : "0 10px 25px -5px rgba(15, 23, 42, 0.04), 0 20px 48px -10px rgba(15, 23, 42, 0.06)"};
                padding: 2.75rem 2.25rem;
                text-align: center;
                animation: ssoCardEntrance 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                box-sizing: border-box;
              ">
                <!-- Brand Logo Area -->
                <div style="margin-bottom: 1.5rem; user-select: none;">
                  <div style="margin-bottom: 0.5rem;">
                    <img src="${logoSrc}" alt="Saka Sales Logo" style="max-height: 52px; max-width: 220px; object-fit: contain;" />
                  </div>
                  <div style="font-size: 0.725rem; font-weight: 600; letter-spacing: 0.05em; color: ${isDark ? "#94a3b8" : "#64748b"}; text-transform: uppercase;">
                    PT SAKA AGUNG ABADI &bull; SINGLE SIGN-ON
                  </div>
                </div>

                <!-- Spinner Modern SVG -->
                <div style="width: 52px; height: 52px; margin: 0.75rem auto 1.5rem auto; position: relative;">
                  <svg style="width: 100%; height: 100%; animation: ssoSvgRotate 1.4s linear infinite;" viewBox="0 0 50 50">
                    <circle cx="25" cy="25" r="20" fill="none" stroke-width="3.5" stroke="${isDark ? "rgba(59, 130, 246, 0.18)" : "rgba(37, 99, 235, 0.12)"}"></circle>
                    <circle cx="25" cy="25" r="20" fill="none" stroke-width="3.5" stroke="${isDark ? "#3b82f6" : "#2563eb"}" stroke-linecap="round" style="animation: ssoSvgDash 1.4s ease-in-out infinite;"></circle>
                  </svg>
                </div>

                <!-- Text Info -->
                <h5 style="font-size: 1.15rem; font-weight: 700; letter-spacing: -0.015em; color: ${isDark ? "#f8fafc" : "#0f172a"}; margin: 0 0 0.5rem 0;">
                  Memverifikasi Sesi Akun
                </h5>
                <p style="font-size: 0.85rem; line-height: 1.55; color: ${isDark ? "#94a3b8" : "#64748b"}; max-width: 310px; margin: 0 auto;">
                  Sesi login Anda telah berakhir. Sistem sedang mengalihkan sesi Anda kembali ke SSO Portal...
                </p>

                <!-- Divider -->
                <div style="height: 1px; width: 100%; margin: 1.85rem 0 1.25rem 0; background: ${isDark ? "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)" : "linear-gradient(90deg, transparent, rgba(226, 232, 240, 0.85), transparent)"};"></div>

                <!-- Footer Security Badge -->
                <div style="display: inline-flex; align-items: center; justify-content: center; gap: 7px; font-size: 0.735rem; font-weight: 500; color: ${isDark ? "#34d399" : "#059669"}; background: ${isDark ? "rgba(16, 185, 129, 0.12)" : "rgba(16, 185, 129, 0.06)"}; border: 1px solid ${isDark ? "rgba(16, 185, 129, 0.25)" : "rgba(16, 185, 129, 0.16)"}; padding: 5px 14px; border-radius: 9999px; letter-spacing: 0.01em;">
                  <span style="width: 6px; height: 6px; border-radius: 50%; background-color: ${isDark ? "#34d399" : "#10b981"}; animation: ssoPulseDot 2s infinite;"></span>
                  <svg style="width: 14px; height: 14px; stroke: currentColor;" viewBox="0 0 24 24" fill="none" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    <path d="m9 12 2 2 4-4"/>
                  </svg>
                  <span>Protokol Autentikasi SSO Terenkripsi</span>
                </div>
              </div>
            `;
            document.body.appendChild(overlay);
          }

          // 4. Arahkan browser langsung ke endpoint SSO
          window.location.href = ssoUrl;
        }
      }

      return Promise.reject(error);
    }
  );
}
